import React, { useRef, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import cls from 'classnames';

import Autocomplete from '@ott/autocomplete';
import Button from '@ott/button';
import Checkbox from '@ott/checkbox';
import FirstErrorShower from '@ott/first-error-shower';
import Input from '@ott/input';
import { l10n } from '@ott/l10n';
import LoadingDots from '@ott/loading-dots';
import Modal, { CONTENT_SIZE } from '@ott/modal';
import { removeNonNumbers, removeNonLetters } from '@ott/transforms';
import { birthDateFormatter, ruPassportFormatter } from '@ott/utility-formatters';
import KM from '@ott/utility-km';

import { PassengerOption } from './blocks/PassengerOption';
import { PassportPhotoRecognition } from './blocks/PassportPhotoRecognition';
import { FORM_FIELDS, INITIAL_VALUES } from './helpers/constants';
import { documentUnitCodeFormatter, innFormatter } from './helpers/formHelpers';
import { formatFullName } from './helpers/formatFullName';
import { usePasswordRecognitionForm } from './hooks/usePasswordRecognition';
import { useUpgradeForm } from './hooks/useUpgradeForm';
import { FormData } from './types';

import styles from './UpgradeFormModal.scss';

const modalContentOptions = {
  title: l10n('virtualCard.upgradeForm.title'),
  size: CONTENT_SIZE.MEDIUM,
  isAlignedLeft: true,
};

type Props = {
  isOpen?: boolean;
  title?: string;
  preventUserCloseModal?: boolean;
  onClose: VoidFunction;
  onSubmit: (data: FormData) => void;
};

const UpgradeForm = ({ isOpen = true, title, preventUserCloseModal, onClose, onSubmit }: Props) => {
  const formRef = useRef<HTMLFormElement | null>(null);

  const {
    isINNVisible,
    isINNLoading,
    isDirty,
    isLoading,
    isSubmitted,
    preparedErrors,
    submitTime,
    control,
    options,
    setValue,
    handlePassengerSelect,
    onDateChange,
    handleSubmit,
  } = useUpgradeForm({ onSubmit, defaultValues: INITIAL_VALUES });

  const {
    isPassportRecognitionLoading,
    isPassportRecognitionPartial,
    passportRecognitionError,
    isPassportRecognitionSuccess,
    handleUploadPassportPhoto,
    abortUploadPassportPhoto,
  } = usePasswordRecognitionForm({ setValue });

  useEffect(() => {
    KM('Virtual_Card_User_Info_transition');
  }, []);

  const contentOption = useMemo(
    () => (title ? { ...modalContentOptions, title } : modalContentOptions),
    [title]
  );

  return (
    <Modal
      isOpen={isOpen}
      fullScreenOnMobile={true}
      contentOptions={contentOption}
      preventUserClose={preventUserCloseModal}
      onClose={onClose}
    >
      <div data-locator="virtual-card-upgrade-form">
        <form onSubmit={handleSubmit} ref={formRef}>
          <PassportPhotoRecognition
            isLoading={isPassportRecognitionLoading}
            error={passportRecognitionError}
            isSuccess={isPassportRecognitionSuccess}
            isRecognitionPartial={isPassportRecognitionPartial}
            handleUploadPassportPhoto={handleUploadPassportPhoto}
            abortUploadPassportPhoto={abortUploadPassportPhoto}
          />
          <FirstErrorShower
            errors={preparedErrors}
            show={isDirty}
            time={submitTime}
            container={formRef.current}
            useRHFErrors={true}
          />
          <div className={styles.row}>
            <Controller
              name={FORM_FIELDS.LAST_NAME}
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  inputValue={field.value}
                  ref={field.ref}
                  name={field.name}
                  className={styles.field}
                  isDisabled={isLoading}
                  isInvalid={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
                  label={l10n('virtualCard.upgradeForm.fields.lastName.placeholder')}
                  specificLocator="upgrade-card-last-name"
                  parse={removeNonLetters}
                  formatter={formatFullName}
                  options={options}
                  onSelect={handlePassengerSelect}
                  CustomOptionRenderer={PassengerOption}
                  autoComplete="new-password"
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name={FORM_FIELDS.FIRST_NAME}
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  className={styles.field}
                  isDisabled={isLoading}
                  hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
                  label={l10n('virtualCard.upgradeForm.fields.firstName.placeholder')}
                  specificLocators="upgrade-card-first-name"
                  parser={removeNonLetters}
                  formatter={formatFullName}
                />
              )}
            />
          </div>
          <div className={styles.row}>
            <Controller
              name={FORM_FIELDS.MIDDLE_NAME}
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  className={styles.field}
                  isDisabled={isLoading}
                  hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
                  label={l10n('virtualCard.upgradeForm.fields.middleName.placeholder')}
                  specificLocators="upgrade-card-middle-name"
                  parser={removeNonLetters}
                  formatter={formatFullName}
                />
              )}
            />
            <Controller
              name={FORM_FIELDS.BIRTH_DATE}
              control={control}
              render={({ field: { onChange, ...restField }, fieldState }) => (
                <Input
                  {...restField}
                  className={styles.field}
                  isDisabled={isLoading}
                  hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
                  label={l10n('virtualCard.upgradeForm.fields.birthDate.placeholder')}
                  specificLocators="upgrade-card-birth-date"
                  parser={removeNonNumbers}
                  formatter={birthDateFormatter}
                  mask={l10n('virtualCard.upgradeForm.fields.birthDate.mask')}
                  onChange={(e) => {
                    onChange(e);
                    onDateChange(FORM_FIELDS.BIRTH_DATE);
                  }}
                />
              )}
            />
          </div>

          <div className={cls(styles.passportInfo)}>
            <div className={styles.row}>
              <div className={styles.cell}>
                <Controller
                  name={FORM_FIELDS.DOCUMENT_NUMBER}
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      className={styles.field}
                      isDisabled={isLoading}
                      hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
                      label={l10n('virtualCard.upgradeForm.fields.documentNumber.placeholder')}
                      specificLocators="upgrade-card-document-number"
                      parser={removeNonNumbers}
                      formatter={ruPassportFormatter}
                    />
                  )}
                />
              </div>
              <div className={styles.cell}>
                <Controller
                  name={FORM_FIELDS.DOCUMENT_ISSUE_DATE}
                  control={control}
                  render={({ field: { onChange, ...restField }, fieldState }) => (
                    <Input
                      {...restField}
                      className={styles.field}
                      isDisabled={isLoading}
                      hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
                      label={l10n('virtualCard.upgradeForm.fields.documentIssueDate.placeholder')}
                      specificLocators="upgrade-card-document-issue-date"
                      formatter={birthDateFormatter}
                      parser={removeNonNumbers}
                      mask={l10n('virtualCard.upgradeForm.fields.documentIssueDate.mask')}
                      onChange={(e) => {
                        onChange(e);
                        onDateChange(FORM_FIELDS.DOCUMENT_ISSUE_DATE);
                      }}
                    />
                  )}
                />
                <Controller
                  name={FORM_FIELDS.DOCUMENT_UNIT_CODE}
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      className={styles.field}
                      isDisabled={isLoading}
                      hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
                      label={l10n('virtualCard.upgradeForm.fields.documentUnitCode.placeholder')}
                      specificLocators="upgrade-card-document-unit-code"
                      formatter={documentUnitCodeFormatter}
                      parser={removeNonNumbers}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className={styles.row}>
            <Controller
              name={FORM_FIELDS.INN}
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  className={cls(styles.innField, {
                    [styles.visible]: isINNVisible,
                  })}
                  isDisabled={isLoading}
                  hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
                  label={l10n('virtualCard.upgradeForm.fields.inn.placeholder')}
                  specificLocators="upgrade-card-inn"
                  formatter={innFormatter}
                  parser={removeNonNumbers}
                  postfix={isINNLoading ? <LoadingDots className={styles.innLoading} /> : null}
                />
              )}
            />
          </div>
          <Controller
            name={FORM_FIELDS.TAX_OK}
            control={control}
            render={({ field: { value, ...restField }, fieldState }) => (
              <Checkbox
                {...restField}
                isChecked={Boolean(value)}
                className={styles.taxOkCheckbox}
                isDisabled={isLoading}
                hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
                specificLocators="upgrade-card-tax-ok"
              >
                <span className={styles.taxOkCheckboxLabel}>
                  {l10n('virtualCard.upgradeForm.fields.taxOk.label')}
                </span>
              </Checkbox>
            )}
          />
          <div className={styles.buttonContainer}>
            <Button
              type="submit"
              isLoading={isLoading}
              withFullWidth={true}
              specificLocators="virtual-card-upgrade-form-action"
            >
              {l10n('virtualCard.upgradeForm.action')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default React.memo(UpgradeForm);
